import { Trans, useLingui } from '@lingui/react/macro';
import { getChampionshipAnnouncedTeams } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  announcedChampionshipTeamsIsLoadingSelector,
  updatingChampionshipTeamSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UDText } from 'ud-ui/components';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import ChampionshipApplicationFilters, {
  ChampionshipApplicationFiltersOption,
  ChampionshipTeamsFilterValues,
} from '../championship-application-filters/championship-application-filters';
import { useUnclaimedTeams } from 'modules/teams/ui/hooks/use-unclaimed-teams';
import ChampionshipTeamCard from './championship-team-card';
import ChampionshipTeamsSkeleton from './championship-teams-skeleton';
import UnclaimedTeamsList from './unclaimed-teams-list';
import { theme } from 'styles/theme/theme';

const statusItems: ChampionshipApplicationFiltersOption<AnnouncedTeamStatus>[] = [
  {
    status: AnnouncedTeamStatus.PENDING,
    color: '#000000',
  },
  {
    status: AnnouncedTeamStatus.ACCEPTED,
    color: '#58B879',
  },
  {
    status: AnnouncedTeamStatus.DECLINED,
    color: theme.colors.error,
  },
];

interface ChampionshipApplicationsProps {
  championshipId: number;
}

const ChampionshipApplications = ({ championshipId }: ChampionshipApplicationsProps) => {
  const { t } = useLingui();
  const dispatch = useDispatch();

  const [status, selectStatus] = useState<AnnouncedTeamStatus>(AnnouncedTeamStatus.PENDING);
  const [name, setName] = useState<string>('');

  const isLoading = useSelector(announcedChampionshipTeamsIsLoadingSelector);
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const updatingTeamId = useSelector(updatingChampionshipTeamSelector);

  useEffect(() => {
    if (!announcedTeams) {
      dispatch(getChampionshipAnnouncedTeams({ championshipId }));
    }
  }, [championshipId, dispatch, announcedTeams]);

  const handleFilterChange = useCallback((values: ChampionshipTeamsFilterValues<AnnouncedTeamStatus>) => {
    setName(values.name);
    selectStatus(values.status);
  }, []);

  const announcedTeamIds = useMemo(() => Object.keys(announcedTeams || {}).map(Number), [announcedTeams]);

  // Команды организации, ещё не привязанные к турниру — тот же источник, что и у
  // `UnclaimedTeamsList` ниже: без него счётчик «Незаявленные» знал только про уже
  // привязанные команды и показывал 0, пока список уже показывал команды организации.
  const { availableTeams: unclaimedTeams } = useUnclaimedTeams(announcedTeamIds);

  const teamsApplicationsList = useMemo(() => {
    return Object.values(announcedTeams || [])
      .filter(
        (team) => status === team.status || (status === AnnouncedTeamStatus.PENDING && team.status === AnnouncedTeamStatus.ANNOUNCED),
      )
      .filter((team) => team.name.toLowerCase().includes(name.toLowerCase()));
  }, [announcedTeams, status, name]);

  // На вкладке «Незаявленные» список пуст, только если пусты обе его части: и уже
  // привязанные, но ещё не решённые команды, и команды организации из `UnclaimedTeamsList`.
  const isListEmpty = teamsApplicationsList.length === 0 && (status !== AnnouncedTeamStatus.PENDING || unclaimedTeams.length === 0);

  const countByStatus = useMemo(() => {
    return Object.values(announcedTeams || []).reduce<Record<string, number>>(
      (result, item) => {
        if (item.status === AnnouncedTeamStatus.ANNOUNCED) {
          const count = result[AnnouncedTeamStatus.PENDING] || 0;
          return {
            ...result,
            [AnnouncedTeamStatus.PENDING]: count + 1,
          };
        }

        const count = result[item.status] || 0;
        return {
          ...result,
          [item.status]: count + 1,
        };
      },
      {
        // Незаявленные команды — это ещё и команды организации, которые вообще не
        // привязаны к турниру: их показывает `UnclaimedTeamsList` вместо пустого
        // состояния, а значит и счётчик обязан их учитывать.
        [AnnouncedTeamStatus.PENDING]: unclaimedTeams.length,
        [AnnouncedTeamStatus.ACCEPTED]: 0,
        [AnnouncedTeamStatus.DECLINED]: 0,
      },
    );
  }, [announcedTeams, unclaimedTeams.length]);

  return (
    <div className="my-4 w-full px-6">
      <UDText type="headline" className="mb-12 shrink-0" style={{ fontSize: 34 }}>
        <Trans>Форма для заявки команд</Trans>
      </UDText>
      {isLoading ? (
        <ChampionshipTeamsSkeleton />
      ) : (
        <>
          <div className="mb-5" style={{ backgroundColor: 'white', borderRadius: 8, padding: 20 }}>
            <ChampionshipApplicationFilters<AnnouncedTeamStatus>
              selectedStatus={status}
              name={name}
              options={statusItems}
              inputPlaceholder={t`Поиск по команде`}
              countByStatus={countByStatus}
              onChange={handleFilterChange}
            />
          </div>

          <div>
            {isListEmpty ? (
              <div className="flex-center h-full">
                <UDText type="body-text" className="text-surfaceRomanSilver30">
                  <Trans>Список пуст</Trans>
                </UDText>
              </div>
            ) : (
              <>
                {teamsApplicationsList.map((team) => (
                  <ChampionshipTeamCard
                    key={`${team.id}-${team.name}`}
                    championshipTeam={team}
                    championshipId={championshipId}
                    isUpdating={updatingTeamId === team.id}
                  />
                ))}
                {status === AnnouncedTeamStatus.PENDING && unclaimedTeams.length > 0 && (
                  <UnclaimedTeamsList championshipId={championshipId} excludeTeamIds={announcedTeamIds} />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChampionshipApplications;
